import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import {
	Grid,
	RadioGroup,
	Radio,
	FormControlLabel,
	GridProps,
} from "@material-ui/core";

// NPM
// import moment from "moment";
import clsx from "clsx";
import { animated } from "react-spring";

// Contexts
import { useApplicationManager } from "../../contexts/ApplicationManager.Context";

// Components
import FormattedFieldSet from "../common/FormattedFieldSet.Component";
import StyledPromptedTextField from "../common/PromptedTextField.Component";

// Classes
import Utility from "../../domain/classes/Utility";
import ITelehealthDay from "../../domain/interfaces/ITelehealthDay";
import Doctor from "../../domain/classes/Doctor";
import IDoctor from "../../domain/interfaces/IDoctor";
import moment from "moment";

export const styles = (theme: Theme) =>
	createStyles({
		root: {},
	});

export interface AttendancePreferenceFieldsContainerProps extends GridProps {}

const AttendancePreferenceFieldsContainer = React.forwardRef<
	HTMLDivElement,
	AttendancePreferenceFieldsContainerProps & WithStyles<typeof styles>
>(({ classes, className, ...gridProps }, ref) => {
	const {
		patientDetails,
		handlePatientDetailsChange,
		clearPatientValidationError,
		patientValidationError,
		selectedDoctor,
		selectedApptType,
		appointmentBookingRequest,
	} = useApplicationManager();

	const [provideOnsite, setProvideOnsite] = React.useState<boolean>(false);
	const [provideTelehealth, setProvideTelehealth] =
		React.useState<boolean>(false);
	const [onsiteAvailable, setOnsiteAvailable] =
		React.useState<boolean>(false);
	const [telehealthAvailable, setTelehealthAvailable] =
		React.useState<boolean>(false);

	const handleAttendancePreferenceChange = (
		event: React.ChangeEvent<HTMLInputElement>
	) => {
		const value: string = (event.target as HTMLInputElement)
			.value as string;

		handlePatientDetailsChange("attendancePreference", value);
		handlePatientDetailsChange("isTelehealth", value === "telehealth");
	};

	const handleReasonForVisitChange = (
		event: React.FocusEvent<HTMLInputElement>
	) => {
		handlePatientDetailsChange("notes", event.target.value as string);
	};

	React.useEffect(() => {
		setProvideOnsite(
			Doctor.provides(
				"onsiteAlso",
				(selectedDoctor as IDoctor).TelehealthDays as ITelehealthDay[],
				moment(appointmentBookingRequest.appointmentDate).isoWeekday()
			)
		);
	}, [selectedDoctor, appointmentBookingRequest.appointmentDate]);

	React.useEffect(() => {
		if (provideTelehealth && telehealthAvailable) {
			handlePatientDetailsChange("attendancePreference", "telehealth");
			handlePatientDetailsChange("isTelehealth", true);
		}
		if (provideOnsite && onsiteAvailable) {
			handlePatientDetailsChange("attendancePreference", "on-site");
			handlePatientDetailsChange("isTelehealth", false);
			handlePatientDetailsChange(
				"telehealthRestrictionAcknowledged",
				false
			);
		}
	}, [
		provideTelehealth,
		provideOnsite,
		handlePatientDetailsChange,
		telehealthAvailable,
		onsiteAvailable,
	]);

	React.useEffect(() => {
		const val =
			selectedApptType.deliveryMethod === 1 ||
			selectedApptType.deliveryMethod === 2;

		setOnsiteAvailable(val);
	}, [selectedApptType]);

	React.useEffect(() => {
		const val =
			selectedApptType.deliveryMethod === 1 ||
			selectedApptType.deliveryMethod === 3;

		setTelehealthAvailable(val);
	}, [selectedApptType]);

	React.useEffect(() => {
		setProvideTelehealth(
			Doctor.provides(
				"telehealthAlso",
				(selectedDoctor as IDoctor).TelehealthDays as ITelehealthDay[],
				moment(appointmentBookingRequest.appointmentDate).isoWeekday()
			)
		);
	}, [selectedDoctor, appointmentBookingRequest.appointmentDate]);

	// const onsiteAvailable: boolean =
	// 	((selectedDoctor as IDoctor).TelehealthDays as ITelehealthDay[]).filter(
	// 		(telehealthDay: ITelehealthDay) =>
	// 			moment(
	// 				appointmentBookingRequest.appointmentStartTime
	// 			).isoWeekday() === telehealthDay.value && telehealthDay.checked
	// 	).length > 0;

	return (
		<Grid
			container
			spacing={1}
			ref={ref}
			{...gridProps}
			className={clsx(classes.root, className)}
		>
			<Grid item xs={12}>
				<FormattedFieldSet label="Attendance preference">
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<RadioGroup
								aria-label="Attendance preference"
								name="attendance-preference"
								value={patientDetails.attendancePreference}
								onChange={handleAttendancePreferenceChange}
							>
								<Grid container spacing={1}>
									<Grid item xs={6}>
										<FormControlLabel
											value="on-site"
											control={
												<Radio
													value="on-site"
													color="primary"
													size="small"
													// disabled={onsiteAvailable}
													disabled={
														!provideOnsite ||
														!onsiteAvailable
													}
												/>
											}
											label="On-site"
										/>
									</Grid>
									<Grid item xs={6}>
										<FormControlLabel
											value="telehealth"
											control={
												<Radio
													value="telehealth"
													color="primary"
													size="small"
													disabled={
														!provideTelehealth ||
														!telehealthAvailable
													}
												/>
											}
											label="Telehealth"
										/>
									</Grid>
								</Grid>
							</RadioGroup>
						</Grid>
						<Grid item xs={12}>
							<StyledPromptedTextField
								title={`What is ${
									patientDetails.isPersonBooking
										? "your"
										: "the patient's"
								} primary medical complaint?`}
								fullWidth
								variant="outlined"
								label="Reason for visit"
								size="small"
								onBlur={handleReasonForVisitChange}
								onFocus={() =>
									clearPatientValidationError("notesError")
								}
								error={!!patientValidationError.notesError}
								helperText={patientValidationError.notesError}
								defaultValue={patientDetails.notes}
							/>
						</Grid>
					</Grid>
				</FormattedFieldSet>
			</Grid>
		</Grid>
	);
});

const StyledAttendancePreferenceFieldsContainer = withStyles(styles, {
	name: Utility.generateClassName("AttendancePreferenceFieldsContainer"),
})(AttendancePreferenceFieldsContainer);

export default StyledAttendancePreferenceFieldsContainer;

export const AnimatedAttendancePreferenceFieldsContainer = animated(
	StyledAttendancePreferenceFieldsContainer
);
